import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "./components/ScrollToTop";
import { QuestionnaireProvider } from "./components/questionnaire/QuestionnaireContext";
import { useAnalyticsPageviews } from "./hooks/use-analytics-pageviews";

// Toast layers are only needed after an interaction — keep them out of the
// initial bundle so the first paint ships less JavaScript.
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));


// Eagerly load homepage (first paint)
import Index from "./pages/Index";

// Lazy-load all other pages for route-based code splitting
const Buy = lazy(() => import("./pages/Buy"));
const Refinance = lazy(() => import("./pages/Refinance"));
const Rates = lazy(() => import("./pages/Rates"));
const LoanPrograms = lazy(() => import("./pages/LoanPrograms"));
const Calculators = lazy(() => import("./pages/Calculators"));
const About = lazy(() => import("./pages/About"));
const Resources = lazy(() => import("./pages/Resources"));
const ApplyNow = lazy(() => import("./pages/ApplyNow"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Buy subpages
const FirstTimeBuyers = lazy(() => import("./pages/buy/FirstTimeBuyers"));
const MortgagePreApproval = lazy(() => import("./pages/buy/MortgagePreApproval"));
const DownPaymentOptions = lazy(() => import("./pages/buy/DownPaymentOptions"));
const DownPaymentAssistance = lazy(() => import("./pages/buy/DownPaymentAssistance"));

// Refinance subpages
const LowerPayment = lazy(() => import("./pages/refinance/LowerPayment"));
const CashOut = lazy(() => import("./pages/refinance/CashOut"));
const ShortenTerm = lazy(() => import("./pages/refinance/ShortenTerm"));

// Rates subpages
const TodaysRates = lazy(() => import("./pages/rates/TodaysRates"));
const RateTrends = lazy(() => import("./pages/rates/RateTrends"));
const WhenToLock = lazy(() => import("./pages/rates/WhenToLock"));

// Loan Programs subpages
const Conventional = lazy(() => import("./pages/loan-programs/Conventional"));
const FHA = lazy(() => import("./pages/loan-programs/FHA"));
const VA = lazy(() => import("./pages/loan-programs/VA"));
const Jumbo = lazy(() => import("./pages/loan-programs/Jumbo"));
const USDA = lazy(() => import("./pages/loan-programs/USDA"));
const ARM = lazy(() => import("./pages/loan-programs/ARM"));
const FHA203k = lazy(() => import("./pages/loan-programs/FHA203k"));
const ReverseMortgage = lazy(() => import("./pages/loan-programs/ReverseMortgage"));
const DSCR = lazy(() => import("./pages/loan-programs/DSCR"));
const BankStatementLoans = lazy(() => import("./pages/loan-programs/BankStatementLoans"));
const ITINLoans = lazy(() => import("./pages/loan-programs/ITINLoans"));
const DoctorLoans = lazy(() => import("./pages/loan-programs/DoctorLoans"));
const NonQMLoansMinnesota = lazy(() => import("./pages/non-qm/NonQMLoansMinnesota"));

// Tools subpages
const MortgagePaymentCalculator = lazy(() => import("./pages/tools/MortgagePaymentCalculator"));
const AffordabilityCalc = lazy(() => import("./pages/tools/AffordabilityCalc"));
const RefinanceCalc = lazy(() => import("./pages/tools/RefinanceCalc"));
const RentVsBuy = lazy(() => import("./pages/tools/RentVsBuy"));
const DSCRCalc = lazy(() => import("./pages/tools/DSCRCalc"));

// Learn subpages
const FirstTimeBuyerGuide = lazy(() => import("./pages/learn/FirstTimeBuyerGuide"));
const ClosingCosts = lazy(() => import("./pages/learn/ClosingCosts"));
const WhatIsAMortgageBroker = lazy(() => import("./pages/learn/WhatIsAMortgageBroker"));
const SecondLookLoanEstimate = lazy(() => import("./pages/SecondLookLoanEstimate"));
const FoundCard = lazy(() => import("./pages/FoundCard"));
const AmyBehning = lazy(() => import("./pages/partners/AmyBehning"));
const PauloMora = lazy(() => import("./pages/partners/PauloMora"));
const AaronBeauchaine = lazy(() => import("./pages/partners/AaronBeauchaine"));
const MustafSahal = lazy(() => import("./pages/partners/MustafSahal"));
const SedatMemetov = lazy(() => import("./pages/partners/SedatMemetov"));
const SusanFeehan = lazy(() => import("./pages/partners/SusanFeehan"));
const WilsonCarpio = lazy(() => import("./pages/partners/WilsonCarpio"));
const DnsCheck = lazy(() => import("./pages/DnsCheck"));
const RealEstateAgents = lazy(() => import("./pages/RealEstateAgents"));
const HilerIQTools = lazy(() => import("./pages/real-estate-agents/HilerIQTools"));
const AgentActiveLoans = lazy(() => import("./pages/real-estate-agents/ActiveLoans"));
const AgentTransactionCoordination = lazy(() => import("./pages/real-estate-agents/TransactionCoordination"));
const AgentServices = lazy(() => import("./pages/real-estate-agents/AgentServices"));

// About subpages
const AboutHiler = lazy(() => import("./pages/about/AboutHiler"));
const Team = lazy(() => import("./pages/about/Team"));
const Contact = lazy(() => import("./pages/about/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Accessibility = lazy(() => import("./pages/Accessibility"));


// Refinance calculator redirect import not needed — uses Navigate
const RefinanceCalculator = lazy(() => import("./pages/refinance/RefinanceCalculator"));

/** Mounted inside <BrowserRouter> so it can use useLocation(). */
const AnalyticsPageviews = () => {
  useAnalyticsPageviews();
  return null;
};

const App = () => (
  <TooltipProvider>
      <Suspense fallback={null}>
        <Toaster />
        <Sonner />
      </Suspense>
      <BrowserRouter>

        <ScrollToTop />
        <AnalyticsPageviews />
        <QuestionnaireProvider>
        <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={<Index />} />

          {/* Buy */}
          <Route path="/buy" element={<Buy />} />
          <Route path="/buy/first-time-buyers" element={<FirstTimeBuyers />} />
          <Route path="/buy/pre-approval" element={<MortgagePreApproval />} />
          <Route path="/buy/down-payment-options" element={<DownPaymentOptions />} />
          <Route path="/buy/down-payment-assistance" element={<DownPaymentAssistance />} />

          {/* Refinance */}
          <Route path="/refinance" element={<Refinance />} />

          {/* Redirects for old routes */}
          <Route path="/buying-power" element={<Navigate to="/calculators/affordability" replace />} />
          <Route path="/buy/affordability-calculator" element={<Navigate to="/calculators/affordability" replace />} />
          <Route path="/refinance/lower-payment" element={<LowerPayment />} />
          <Route path="/refinance/cash-out" element={<CashOut />} />
          <Route path="/refinance/shorten-term" element={<ShortenTerm />} />
          <Route path="/refinance/calculator" element={<Navigate to="/calculators/refinance" replace />} />

          {/* Rates */}
          <Route path="/rates" element={<Rates />} />
          <Route path="/rates/today" element={<TodaysRates />} />
          <Route path="/rates/understanding-rates" element={<RateTrends />} />
          <Route path="/rates/when-to-lock" element={<WhenToLock />} />

          {/* Loan Programs */}
          <Route path="/loan-programs" element={<LoanPrograms />} />
          <Route path="/loan-programs/conventional" element={<Conventional />} />
          <Route path="/loan-programs/fha" element={<FHA />} />
          <Route path="/loan-programs/va" element={<VA />} />
          <Route path="/loan-programs/jumbo" element={<Jumbo />} />
          <Route path="/loan-programs/usda" element={<USDA />} />
          <Route path="/loan-programs/arm" element={<ARM />} />
          <Route path="/loan-programs/fha-203k" element={<FHA203k />} />
          <Route path="/loan-programs/reverse-mortgage" element={<ReverseMortgage />} />
          <Route path="/loan-programs/dscr" element={<DSCR />} />
          <Route path="/loan-programs/bank-statement-loans" element={<BankStatementLoans />} />
          <Route path="/loan-programs/itin-loans" element={<ITINLoans />} />
          <Route path="/loan-programs/doctor-loans" element={<DoctorLoans />} />
          <Route path="/non-qm-loans-minnesota" element={<NonQMLoansMinnesota />} />

          {/* Tools */}
          <Route path="/calculators" element={<Calculators />} />
          <Route path="/calculators/mortgage-payment" element={<MortgagePaymentCalculator />} />
          <Route path="/calculators/affordability" element={<AffordabilityCalc />} />
          <Route path="/calculators/refinance" element={<RefinanceCalc />} />
          <Route path="/calculators/rent-vs-buy" element={<RentVsBuy />} />
          <Route path="/calculators/dscr" element={<DSCRCalc />} />

          {/* Learn */}
          <Route path="/resources" element={<Resources />} />
          <Route path="/learn/first-time-buyer-guide" element={<FirstTimeBuyerGuide />} />
          <Route path="/learn/how-rates-work" element={<Navigate to="/rates/understanding-rates" replace />} />
          <Route path="/rates/trends" element={<Navigate to="/rates/understanding-rates" replace />} />
          <Route path="/learn/pre-approval-explained" element={<Navigate to="/buy/pre-approval" replace />} />
          <Route path="/learn/closing-costs" element={<ClosingCosts />} />
          <Route path="/learn/what-is-a-mortgage-broker" element={<WhatIsAMortgageBroker />} />
          <Route path="/learn/minnesota-down-payment-assistance-programs" element={<Navigate to="/buy/down-payment-assistance" replace />} />
          <Route path="/second-look-loan-estimate" element={<SecondLookLoanEstimate />} />

          {/* About */}
          <Route path="/about" element={<About />} />
          <Route path="/about/hiler-mortgage" element={<AboutHiler />} />
          <Route path="/about/team" element={<Team />} />
          <Route path="/about/contact" element={<Contact />} />

          <Route path="/apply" element={<ApplyNow />} />
          <Route path="/real-estate-agents" element={<RealEstateAgents />} />
          <Route path="/real-estate-agents/hileriq" element={<HilerIQTools />} />
          <Route path="/real-estate-agents/active-loans" element={<AgentActiveLoans />} />
          <Route path="/real-estate-agents/transaction-coordination" element={<AgentTransactionCoordination />} />
          <Route path="/real-estate-agents/services" element={<AgentServices />} />
          <Route path="/found" element={<FoundCard />} />
          <Route path="/partners/amy-behning" element={<AmyBehning />} />
          <Route path="/partners/paulo-mora" element={<PauloMora />} />
          <Route path="/partners/aaron-beauchaine" element={<AaronBeauchaine />} />
          <Route path="/partners/mustaf-sahal" element={<MustafSahal />} />
          <Route path="/partners/sedat-memetov" element={<SedatMemetov />} />
          <Route path="/partners/susan-feehan" element={<SusanFeehan />} />
          <Route path="/partners/wilson-carpio" element={<WilsonCarpio />} />
          <Route path="/dns-check" element={<DnsCheck />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/accessibility" element={<Accessibility />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        </QuestionnaireProvider>
      </BrowserRouter>
  </TooltipProvider>

);

export default App;
